import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { Layer } from 'bernie-view';
import { QueryRoute, updateSearch } from 'bernie-core';

import {
  PropertyInventorySource,
  PropertySpaceOverview,
  PropertySummaryAmenities,
  PropertySummaryHeader,
  PropertySummaryMapTrigger,
  PropertySummaryNearByPois,
  PropertySummaryReviews,
} from '@shared-ui/lodging-property-details';
import { useDialog } from '@shared-ui/dialog-context';
import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';

import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSheet } from 'uitk-react-sheet';
import { UitkSpacing } from 'uitk-react-spacing';

import { SharedUiMap } from 'components/infosite/shared-ui/details/map-wrapper';
import { SharedUiPropertyReviewsDialog } from 'components/infosite/shared-ui/reviews/shared-ui-property-reviews-dialog';
import { SharedUiWrapperProps, withSharedUiPropertyDetails } from 'components/infosite/shared-ui/details/shared-ui-property-details';

const SummaryWrapper: React.FC<SharedUiWrapperProps> = React.memo(({ inputs }) => {
  const history = useHistory();
  const location = useLocation();

  const onClickMap = React.useCallback(() => updateSearch({ history, location, newParams: { pwaDialog: 'map' } }), [history, location]);

  const onClickReviews = React.useCallback(
    () => updateSearch({ history, location, newParams: { pwaDialog: 'reviews' } }),
    [history, location]
  );

  return (
    <UitkSpacing margin={{ blockend: 'six', blockstart: 'half' }}>
      <UitkCard className="uitk-flat-border-top">
        <UitkCardContentSection>
          <UitkSpacing padding={{ large: { inline: 'three', block: 'three' } }}>
            <UitkLayoutFlex direction="column">
              <>
                <UitkLayoutFlexItem>
                  <UitkSpacing padding={{ blockend: 'four' }}>
                    <div data-stid="content-hotel-title">
                      <PropertySummaryHeader inputs={inputs} />
                    </div>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkSpacing padding={{ blockend: 'six' }}>
                    <PropertySummaryReviews inputs={inputs} onClickReviews={onClickReviews} />
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkSpacing>
                    <PropertySpaceOverview inputs={inputs} />
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkSpacing margin={{ blockstart: 'four' }}>
                    <div>
                      <PropertyInventorySource inputs={inputs} />
                    </div>
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkSpacing padding={{ blockstart: 'six', blockend: 'six' }} margin={{ block: 'three' }}>
                    <PropertySummaryAmenities inputs={inputs} />
                  </UitkSpacing>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <div>
                    <Viewport>
                      <ViewSmall>
                        <>
                          <div data-stid="map-image-link">
                            <UitkSpacing padding={{ small: { blockend: 'six' } }}>
                              <PropertySummaryMapTrigger
                                inputs={inputs}
                                onClickMap={onClickMap}
                                aspectRatio={UitkFigureAspectRatioType.R21_9}
                              />
                            </UitkSpacing>
                          </div>
                          <div data-stid="location-summary">
                            <UitkSpacing padding={{ small: { blockend: 'six' } }}>
                              <PropertySummaryNearByPois inputs={inputs} />
                            </UitkSpacing>
                          </div>
                        </>
                      </ViewSmall>
                      <ViewMedium>
                        <UitkLayoutGrid space="three" columns={2}>
                          <UitkLayoutGridItem>
                            <div data-stid="map-image-link">
                              <PropertySummaryMapTrigger
                                inputs={inputs}
                                onClickMap={onClickMap}
                                aspectRatio={UitkFigureAspectRatioType.R21_9}
                              />
                            </div>
                          </UitkLayoutGridItem>
                          <UitkLayoutGridItem>
                            <div data-stid="location-summary">
                              <PropertySummaryNearByPois inputs={inputs} />
                            </div>
                          </UitkLayoutGridItem>
                        </UitkLayoutGrid>
                      </ViewMedium>
                    </Viewport>
                  </div>
                </UitkLayoutFlexItem>
              </>
            </UitkLayoutFlex>
          </UitkSpacing>
        </UitkCardContentSection>
        <MapDialogWrapper />
        <ReviewsDialogWrapper />
      </UitkCard>
    </UitkSpacing>
  );
});

interface SummaryDialogProps {
  currentHotel?: CurrentHotelStore;
}

const ReviewsDialogWrapper: React.FC<SummaryDialogProps> = () => {
  const [isDialogOpen, dialogActions, DialogComponent] = useDialog('reviews', 'pwaDialog');
  const onDialogClose = () => {
    dialogActions.closeDialog();
  };

  return (
    <DialogComponent>
      <UitkSheet type="full" isVisible={isDialogOpen} triggerRef={null} returnFocusOnClose>
        <SharedUiPropertyReviewsDialog onDialogClose={onDialogClose} />
      </UitkSheet>
    </DialogComponent>
  );
};
ReviewsDialogWrapper.displayName = 'ReviewsDialogWrapper';

const MapDialogWrapper: React.FC<SummaryDialogProps> = () => {
  const history = useHistory();

  const onCloseDialog = React.useCallback(() => history.goBack(), [history]);

  return (
    <QueryRoute query={{ pwaDialog: 'map' }}>
      <Layer id="infosite-map" onDismiss={onCloseDialog}>
        <SharedUiMap onClose={onCloseDialog} />
      </Layer>
    </QueryRoute>
  );
};
MapDialogWrapper.displayName = 'MapDialogWrapper';

export const SingleOfferSummaryWrapper = withSharedUiPropertyDetails<SharedUiWrapperProps>(SummaryWrapper);
SingleOfferSummaryWrapper.displayName = 'SingleOfferSummaryWrapper';
